/**
 * Carte cadeau — maquette interactive (V1, sans backend).
 *
 *  Mode 1 « présentation » : carte officielle Bocuse en flip 3D + CTA d'achat.
 *  Mode 2 « parcours d'achat » : wizard 4 étapes (montant → destinataire →
 *  message → récapitulatif) puis écran de confirmation mockup + aperçu Wallet.
 *
 * Aucun paiement réel, aucune persistance : tout est en état local. Les paliers,
 * bornes min/max et exclusions viennent du tenant config (section giftCard) —
 * jamais de `if (tenant === …)`.
 */
import { useMemo, useState } from 'react';
import { useI18n } from '../i18n';
import { PageHeader } from '../components/PageHeader';
import { GiftCardVisual } from '../components/GiftCardVisual';
import tenantConfig from '../../tenant.config.json';
import './GiftCardScreen.css';

const GIFT = tenantConfig.giftCard;
const MESSAGE_MAX = 200;

type Mode = 'present' | 'purchase';
type RecipientType = 'other' | 'self';

/** yyyy-mm-dd du jour, en heure locale (pour la valeur par défaut de l'input date). */
function todayISO(): string {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
}

export function GiftCardScreen() {
  const { t, lang } = useI18n();

  const [mode, setMode] = useState<Mode>('present');
  const [step, setStep] = useState(1); // 1..4
  const [confirmed, setConfirmed] = useState(false);
  const [showWallet, setShowWallet] = useState(false);

  // — données du parcours —
  const [preset, setPreset] = useState<number | null>(GIFT.presets[1] ?? GIFT.presets[0]);
  const [isCustom, setIsCustom] = useState(false);
  const [custom, setCustom] = useState('');
  const [recipientType, setRecipientType] = useState<RecipientType>('other');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [date, setDate] = useState(todayISO());
  const [message, setMessage] = useState('');

  const amount = useMemo<number | null>(() => {
    if (isCustom) {
      const n = Number(custom);
      if (!Number.isFinite(n) || n < GIFT.minAmount || n > GIFT.maxAmount) return null;
      return Math.round(n);
    }
    return preset;
  }, [isCustom, custom, preset]);

  const fmtAmount = (n: number) => `${n.toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-GB')} €`;
  const fmtDate = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

  const recipientLabel = recipientType === 'self' ? t('gc_review_recipient_self') : name.trim() || '—';

  // — navigation —
  const resetToPresent = () => {
    setMode('present');
    setStep(1);
    setConfirmed(false);
    setShowWallet(false);
  };
  const startPurchase = () => {
    setConfirmed(false);
    setStep(1);
    setMode('purchase');
  };
  const goBack = () => {
    if (confirmed) return resetToPresent();
    if (step === 1) return resetToPresent();
    setStep((s) => s - 1);
  };
  const goNext = () => setStep((s) => Math.min(4, s + 1));

  // — validation par étape (le bouton Continuer reste cliquable, on guide par message) —
  const canContinue = (() => {
    if (step === 1) return amount !== null;
    return true; // étapes 2/3 facultatives en maquette
  })();

  // ───────────────────────── Mode 1 : présentation ─────────────────────────
  if (mode === 'present') {
    return (
      <div className="mb-gc">
        <PageHeader title={t('gc_eyebrow')} />
        <main className="mb-gc-present">
          <p className="mb-gc-overline">{t('gc_eyebrow')}</p>
          <h2 className="mb-gc-tagline">{t('gc_present_tagline')}</h2>

          <GiftCardVisual />

          <div className="mb-gc-present-meta">
            <span className="mb-gc-badge">✦ {t('gc_reloadable')}</span>
            <span className="mb-gc-hint">{t('gc_flip_hint')}</span>
          </div>

          <button type="button" className="mb-gc-cta" onClick={startPurchase}>
            {t('gc_cta_buy')}
          </button>
        </main>
      </div>
    );
  }

  // ───────────────────────── Mode 2 : confirmation ─────────────────────────
  if (confirmed) {
    return (
      <div className="mb-gc mb-gc--center">
        <main className="mb-gc-confirm">
          <p className="mb-gc-overline">{t('gc_confirm_eyebrow')}</p>
          <h2 className="mb-gc-confirm-thanks">{t('gc_confirm_thanks')}</h2>
          <p className="mb-gc-confirm-body">
            {recipientType === 'self' ? (
              <>{t('gc_confirm_to_you')}</>
            ) : (
              <>
                {t('gc_confirm_sent_to')} <strong>{recipientLabel}</strong> {t('gc_confirm_on')}{' '}
                <strong>{fmtDate(date)}</strong>
              </>
            )}
            {amount !== null && <> — {fmtAmount(amount)}.</>}
          </p>

          <p className="mb-gc-confirm-quote">{t('card_quote')}</p>
          <p className="mb-gc-confirm-sign">Paul Bocuse</p>

          <div className="mb-gc-confirm-actions">
            <button type="button" className="mb-gc-cta" onClick={() => setShowWallet(true)}>
              {t('gc_confirm_wallet_btn')}
            </button>
            <button type="button" className="mb-gc-ghost" onClick={resetToPresent}>
              {t('gc_confirm_done')}
            </button>
          </div>

          <p className="mb-gc-mockup-note">{t('gc_confirm_mockup_note')}</p>
        </main>

        {showWallet && (
          <WalletPreview
            amount={amount}
            fmtAmount={fmtAmount}
            onClose={() => setShowWallet(false)}
          />
        )}
      </div>
    );
  }

  // ───────────────────────── Mode 2 : wizard ─────────────────────────
  return (
    <div className="mb-gc">
      <header className="mb-gc-wizard-head">
        <button type="button" className="mb-gc-back" onClick={goBack}>
          <span aria-hidden="true">←</span> {t('back')}
        </button>
        <span className="mb-gc-step-label">
          {t('gc_step')} {step} / 4
        </span>
        <button type="button" className="mb-gc-cancel" onClick={resetToPresent}>
          {t('gc_cancel')}
        </button>
      </header>

      <div className="mb-gc-progress" aria-hidden="true">
        {[1, 2, 3, 4].map((i) => (
          <span key={i} className={`mb-gc-progress-bar${i <= step ? ' is-on' : ''}`} />
        ))}
      </div>

      <main className="mb-gc-wizard">
        {/* — Étape 1 : montant — */}
        {step === 1 && (
          <section className="mb-gc-step">
            <h2 className="mb-gc-step-title">{t('gc_amount_title')}</h2>
            <div className="mb-gc-presets">
              {GIFT.presets.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`mb-gc-preset${!isCustom && preset === p ? ' is-active' : ''}`}
                  onClick={() => {
                    setIsCustom(false);
                    setPreset(p);
                  }}
                >
                  {fmtAmount(p)}
                </button>
              ))}
              <button
                type="button"
                className={`mb-gc-preset mb-gc-preset--other${isCustom ? ' is-active' : ''}`}
                onClick={() => setIsCustom(true)}
              >
                {t('gc_amount_other')}
              </button>
            </div>

            {isCustom && (
              <div className="mb-gc-field">
                <label htmlFor="gc-custom">{t('gc_amount_custom_label')}</label>
                <div className="mb-gc-amount-input">
                  <input
                    id="gc-custom"
                    type="number"
                    inputMode="numeric"
                    min={GIFT.minAmount}
                    max={GIFT.maxAmount}
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder={String(GIFT.minAmount)}
                    autoFocus
                  />
                  <span aria-hidden="true">€</span>
                </div>
                <p className="mb-gc-help">
                  {t('gc_amount_range_prefix')} {fmtAmount(GIFT.minAmount)} – {fmtAmount(GIFT.maxAmount)}
                </p>
              </div>
            )}
          </section>
        )}

        {/* — Étape 2 : destinataire — */}
        {step === 2 && (
          <section className="mb-gc-step">
            <h2 className="mb-gc-step-title">{t('gc_recipient_title')}</h2>
            <div className="mb-gc-choices">
              <button
                type="button"
                className={`mb-gc-choice${recipientType === 'other' ? ' is-active' : ''}`}
                onClick={() => setRecipientType('other')}
                aria-pressed={recipientType === 'other'}
              >
                <span className="mb-gc-choice-title">{t('gc_recipient_other')}</span>
                <span className="mb-gc-choice-sub">{t('gc_recipient_other_sub')}</span>
              </button>
              <button
                type="button"
                className={`mb-gc-choice${recipientType === 'self' ? ' is-active' : ''}`}
                onClick={() => setRecipientType('self')}
                aria-pressed={recipientType === 'self'}
              >
                <span className="mb-gc-choice-title">{t('gc_recipient_self')}</span>
                <span className="mb-gc-choice-sub">{t('gc_recipient_self_sub')}</span>
              </button>
            </div>

            {recipientType === 'other' ? (
              <div className="mb-gc-fields">
                <div className="mb-gc-field">
                  <label htmlFor="gc-name">{t('gc_recipient_name')}</label>
                  <input
                    id="gc-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('gc_recipient_name_ph')}
                  />
                </div>
                <div className="mb-gc-field">
                  <label htmlFor="gc-email">{t('gc_recipient_email')}</label>
                  <input
                    id="gc-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('gc_recipient_email_ph')}
                  />
                </div>
                <div className="mb-gc-field">
                  <label htmlFor="gc-date">{t('gc_recipient_date')}</label>
                  <input
                    id="gc-date"
                    type="date"
                    value={date}
                    min={todayISO()}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <div className="mb-gc-fields">
                <div className="mb-gc-field">
                  <label htmlFor="gc-self-email">{t('gc_recipient_self_email')}</label>
                  <input
                    id="gc-self-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('gc_recipient_email_ph')}
                  />
                </div>
              </div>
            )}
          </section>
        )}

        {/* — Étape 3 : message — */}
        {step === 3 && (
          <section className="mb-gc-step">
            <h2 className="mb-gc-step-title">{t('gc_message_title')}</h2>
            <div className="mb-gc-field">
              <div className="mb-gc-field-row">
                <label htmlFor="gc-message">{t('gc_review_message')}</label>
                <span className="mb-gc-optional">{t('gc_message_optional')}</span>
              </div>
              <textarea
                id="gc-message"
                rows={4}
                maxLength={MESSAGE_MAX}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('gc_message_ph')}
              />
              <p className="mb-gc-counter">
                {message.length}/{MESSAGE_MAX}
              </p>
            </div>
          </section>
        )}

        {/* — Étape 4 : récapitulatif — */}
        {step === 4 && (
          <section className="mb-gc-step">
            <h2 className="mb-gc-step-title">{t('gc_review_title')}</h2>
            <div className="mb-gc-summary">
              <Row label={t('gc_review_amount')} value={amount !== null ? fmtAmount(amount) : '—'} strong />
              <Row label={t('gc_review_recipient')} value={recipientLabel} />
              {email.trim() && <Row label="Email" value={email.trim()} />}
              <Row
                label={t('gc_review_date')}
                value={recipientType === 'self' ? t('gc_review_date_now') : fmtDate(date)}
              />
              <Row
                label={t('gc_review_message')}
                value={message.trim() || t('gc_review_message_none')}
                muted={!message.trim()}
              />
            </div>
            <p className="mb-gc-review-note">{t('gc_review_note')}</p>
          </section>
        )}
      </main>

      <footer className="mb-gc-footer">
        {step === 1 && amount === null && <p className="mb-gc-foot-error">{t('gc_amount_invalid')}</p>}
        {step < 4 ? (
          <button type="button" className="mb-gc-cta" disabled={!canContinue} onClick={goNext}>
            {t('gc_continue')}
          </button>
        ) : (
          <button type="button" className="mb-gc-cta" onClick={() => setConfirmed(true)}>
            {t('gc_pay')}
          </button>
        )}
      </footer>
    </div>
  );
}

/** Ligne d'un récapitulatif (label gauche / valeur droite). */
function Row({
  label,
  value,
  strong,
  muted,
}: {
  label: string;
  value: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="mb-gc-row">
      <span className="mb-gc-row-label">{label}</span>
      <span className={`mb-gc-row-value${strong ? ' is-strong' : ''}${muted ? ' is-muted' : ''}`}>
        {value}
      </span>
    </div>
  );
}

/** Aperçu statique du futur pass Apple/Google Wallet. */
function WalletPreview({
  amount,
  fmtAmount,
  onClose,
}: {
  amount: number | null;
  fmtAmount: (n: number) => string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="mb-gc-wallet-overlay" role="dialog" aria-modal="true" aria-label={t('gc_wallet_title')}>
      <div className="mb-gc-wallet-sheet">
        <p className="mb-gc-overline">{t('gc_wallet_title')}</p>
        <div className="mb-gc-pass">
          <div className="mb-gc-pass-top">
            <span className="mb-gc-pass-brand">MAISONS BOCUSE</span>
            <span className="mb-gc-pass-sign">Bocuse</span>
          </div>
          <div className="mb-gc-pass-value">{amount !== null ? fmtAmount(amount) : '—'}</div>
          <div className="mb-gc-pass-bottom">
            <span>{t('gc_wallet_value')}</span>
            <span className="mb-gc-pass-code">CODE 4444 · N° 12345</span>
          </div>
        </div>
        <p className="mb-gc-wallet-soon">{t('gc_wallet_soon')}</p>
        <button type="button" className="mb-gc-ghost" onClick={onClose}>
          {t('gc_wallet_close')}
        </button>
      </div>
    </div>
  );
}
